import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Search,
  Filter,
  Hash,
  Loader2,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAPIClient } from '@/hooks/useAPIClient';
import { FewShotExample, ExampleListFilters } from '@/types/fewShot';
import { ExampleRow } from './ExampleRow';
import { getMachineLabel } from '@/constants/machines';

export function ExampleList() {
  const { toast } = useToast();
  const apiClient = useAPIClient();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'good' | 'bad'>('all');
  const [machineFilter, setMachineFilter] = useState('all');
  const [deleteExample, setDeleteExample] = useState<FewShotExample | null>(null);

  // Build filters
  const filters = useMemo((): ExampleListFilters => {
    const result: ExampleListFilters = {};

    if (filterType === 'good') result.is_good_example = true;
    if (filterType === 'bad') result.is_good_example = false;
    if (machineFilter !== 'all') result.machine_filter = machineFilter;

    return result;
  }, [filterType, machineFilter]);

  // Fetch examples from JSON file
  const { data: fileData, isLoading, error } = useQuery({
    queryKey: ['few-shot', 'examples'],
    queryFn: () => apiClient.fewShot.listExamplesFromFile(),
  });

  // Apply client-side filtering
  const examples = useMemo(() => {
    if (!fileData?.examples) return [];

    let filtered = fileData.examples;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(example =>
        example.question?.toLowerCase().includes(term) ||
        example.answer?.toLowerCase().includes(term) ||
        example.explanation?.toLowerCase().includes(term) ||
        example.machines?.some(m => m.toLowerCase().includes(term)) ||
        example.submachine_part?.toLowerCase().includes(term) ||
        example.error_code?.toLowerCase().includes(term)
      );
    }

    // Apply filters
    if (filters.is_good_example !== undefined) {
      filtered = filtered.filter(example => example.is_good_example === filters.is_good_example);
    }

    if (filters.machine_filter) {
      filtered = filtered.filter(example =>
        example.machines?.includes(filters.machine_filter!)
      );
    }

    if (filters.submachine_part) {
      filtered = filtered.filter(example => example.submachine_part === filters.submachine_part);
    }

    if (filters.error_code) {
      filtered = filtered.filter(example => example.error_code === filters.error_code);
    }

    if (filters.created_by) {
      filtered = filtered.filter(example => example.created_by === filters.created_by);
    }

    return filtered;
  }, [fileData?.examples, filters, searchTerm]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const result = await apiClient.fewShot.deleteExample(id);
      return result;
    },
    onSuccess: async (data) => {
      // Force refetch to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['few-shot', 'examples'] });
      await queryClient.invalidateQueries({ queryKey: ['few-shot', 'stats'] });

      // Wait a bit for backend to save the file
      await new Promise(resolve => setTimeout(resolve, 200));

      await queryClient.refetchQueries({ queryKey: ['few-shot', 'examples'] });

      // Get stats and show consolidated message
      const stats = await apiClient.fewShot.getStatistics();
      toast({
        title: 'Beispiel gelöscht',
        description: `${stats.total_examples} Beispiele in Datenbank`,
      });

      setDeleteExample(null);
    },
    onError: (error: Error) => {
      toast({
        title: 'Löschen fehlgeschlagen',
        description: error.message,
        variant: 'destructive',
      });
      setDeleteExample(null);
    },
  });

  // Filter examples by search term
  const filteredExamples = useMemo(() => {
    if (!searchTerm) return examples;

    const term = searchTerm.toLowerCase();
    return examples.filter(
      (example) =>
        example.question.toLowerCase().includes(term) ||
        example.answer.toLowerCase().includes(term) ||
        example.machines?.some(m => m.toLowerCase().includes(term)) ||
        example.error_code?.toLowerCase().includes(term) ||
        example.tags.some(tag => tag.toLowerCase().includes(term))
    );
  }, [examples, searchTerm]);

  // Get unique machines for filter (flatten all machines arrays)
  const uniqueMachines = useMemo(() => {
    const machines = examples
      .flatMap(ex => ex.machines || [])
      .filter((machine): machine is string => !!machine);
    return Array.from(new Set(machines)).sort();
  }, [examples]);


  const handleDelete = (example: FewShotExample) => {
    setDeleteExample(example);
  };

  const confirmDelete = () => {
    if (deleteExample) {
      deleteMutation.mutate(deleteExample.example_id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">Fehler beim Laden der Beispiele</p>
        <Button
          variant="outline"
          onClick={() => queryClient.invalidateQueries({ queryKey: ['few-shot', 'examples'] })}
          className="mt-2"
        >
          Wiederholen
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Beispiele suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <Select value={filterType} onValueChange={(value: string) => setFilterType(value)}>
          <SelectTrigger className="w-full md:w-48">
            <SelectValue placeholder="Nach Typ filtern" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Beispiele</SelectItem>
            <SelectItem value="good">Gute Beispiele</SelectItem>
            <SelectItem value="bad">Schlechte Beispiele</SelectItem>
          </SelectContent>
        </Select>

        <Select value={machineFilter} onValueChange={setMachineFilter}>
          <SelectTrigger className="w-full md:w-48">
            <SelectValue placeholder="Nach Maschine filtern" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Maschinen</SelectItem>
            {uniqueMachines.map((machine) => (
              <SelectItem key={machine} value={machine}>
                {getMachineLabel(machine)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {filteredExamples.length} Beispiel{filteredExamples.length !== 1 ? 'e' : ''} gefunden
        </p>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Filter className="h-4 w-4" />
          <span>Gefiltert nach: {filterType === 'all' ? 'Alle' : filterType === 'good' ? 'Gut' : 'Schlecht'}</span>
          {machineFilter !== 'all' && <span>, {getMachineLabel(machineFilter)}</span>}
        </div>
      </div>

      {/* Examples List */}
      {filteredExamples.length === 0 ? (
        <div className="text-center py-12">
          <Hash className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Keine Beispiele gefunden</h3>
          <p className="text-muted-foreground mb-4">
            {searchTerm || filterType !== 'all' || machineFilter !== 'all'
              ? 'Versuchen Sie, Ihre Filter oder Suchbegriffe anzupassen.'
              : 'Erstellen Sie Ihr erstes Beispiel, um zu beginnen.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredExamples.map((example) => (
            <ExampleRow
              key={example.example_id}
              example={example}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteExample} onOpenChange={() => setDeleteExample(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Beispiel löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie dieses Beispiel löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
            {deleteExample && (
              <div className="mt-2 p-2 bg-muted rounded">
                <p className="font-medium">Frage:</p>
                <p className="text-sm line-clamp-2">{deleteExample.question}</p>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird gelöscht...
                </>
              ) : (
                'Löschen'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
